import React, { Component, useEffect, useReducer } from 'react'
import { createBrowserHistory } from 'history'
import { runtime } from 'rest/runtime'
import { IntlProvider } from 'react-intl'
import { translationMessages, defaultLocale } from 'i18n'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { DndProvider } from 'react-dnd'
import NiceModal from '@ebay/nice-modal-react'

import CoordinatorApp from './CoordinatorApp'
import SupplierApp from './SupplierApp'
import CustomerApp from './CustomerApp'
import PublicApp from './PublicApp'
import EmbeddedApp from 'containers/EmbeddedApp'

import { CurrentUserContextProvider } from 'hooks/useCurrentUser'
import { RoutesContextProvider } from 'hooks/useRoutes'
import { setTheme, applyTheme } from 'themes/utils'
import moment from 'moment'

import 'react-phone-input-2/lib/plain.css'
import 'react-datetime/css/react-datetime.css';
import 'tailwindcss/tailwind.css'
import 'url-search-params-polyfill'
import 'fontawesome';

const history = createBrowserHistory();

global.reactHistory = history;

export const IntlContext = React.createContext({
  locale: defaultLocale,
  messages: [],
})

const AutorizedApp = (props) => {
  if (window.location.pathname.startsWith('/embedded')) {
    return <EmbeddedApp history={history} {...props} />
  }

  if (runtime.currentUser.isCoordinator()) {
    return <CoordinatorApp history={history} {...props} />
  }

  if (runtime.currentUser.isSupplier()) {
    return <SupplierApp history={history} {...props} />
  }

  if (runtime.currentUser.isCustomer()) {
    return <CustomerApp history={history} userId={runtime.currentUser.get('uid')} {...props} />
  }

  return (
    <PublicApp history={history} {...props} />
  )
}

const App = ({ partner = 'voicearchive', initialLocale = 'en', ...props }) => {
  const [intl, dispatch] = useReducer((state, { action, payload }) => {
    if (action == 'SET_LOCALE') {
      window?.localStorage?.setItem('App.locale', payload)

      return {
        ...state,
        locale: payload,
        messages: {
          ...translationMessages[partner][payload],
          ...state.overwrites,
        }
      }
    }

    if (action == 'OVERWRITE') {
      return {
        ...state,
        messages: { ...state.messages, ...payload },
        overwrites: payload,
      }
    }

    return state
  }, {
    locale: window?.localStorage?.getItem('App.locale') || initialLocale,
    messages: translationMessages[partner][window?.localStorage?.getItem('App.locale') || initialLocale],
    overwrites: {},
  })

  useEffect(() => {
    if (partner === 'voicearchive') {
      applyTheme('base')
    } else {
      applyTheme(partner)
    }
  }, [])

  moment.locale(intl.locale ? intl.locale : defaultLocale)
  
  // Force Monday as the start of the week. This is implemented so that react-datetime calendar starts on Monday
  moment.updateLocale(intl.locale ? intl.locale : defaultLocale, {
    week: {
      dow: 1 // day of week
    }
  })

  return (
    <div className="w-full h-full bg-white">
      <IntlContext.Provider value={{ intl, dispatch }}>
        <IntlProvider
          locale={intl.locale}
          messages={intl.messages}
          defaultLocale={defaultLocale}
        >
          <DndProvider backend={HTML5Backend}>
            <CurrentUserContextProvider>
              <RoutesContextProvider>
                <NiceModal.Provider>
                  <AutorizedApp {...props } partner={partner} />
                </NiceModal.Provider>
              </RoutesContextProvider>
            </CurrentUserContextProvider>
          </DndProvider>
        </IntlProvider>
      </IntlContext.Provider>
    </div>
  )
}

export default App
